import type {
  CommunityReportingThread,
  SocialCommunity,
  SocialDateRange,
} from '../../types/socialReporting.js';
import { buildReviewsReputationForChannels, type ReviewsReputationView } from './community.js';

// Google Business reviews — the reputation snapshot for the GBP tab.
//
// REUSES the community reporting lib's channel-scoped reputation core (the same
// period-scoped average-rating gate, star distribution, reply rate, rating trend
// and recent-reviews list), scoped to the googlebusiness channel. Splitting the
// reputation this way is the reviews-split rule: Google review reputation lives on
// the Google Business tab, Facebook review reputation stays on Community. The
// responsiveness metrics (how fast we replied) stay on the Community tab across
// ALL channels — only the reputation SNAPSHOT relocates.
//
// ⚠️ The rating is PERIOD-SCOPED, never a claimed lifetime Google rating — that
// honesty gate is enforced (and mutation-tested) in community.ts and inherited
// here unchanged (we hold only a recent ingested slice; the batch endpoint omits
// the true aggregate).

/**
 * Build the Google-review reputation view from the GBP payload's review threads
 * (already scoped to channel googlebusiness + kind review by the server) over the
 * union window, split into current vs previous by the range. Delegates to the
 * community core with a ['googlebusiness'] channel filter so a stray non-Google
 * review can never leak in.
 */
export function buildGoogleReviewsReputation(
  reviewThreads: CommunityReportingThread[],
  range: SocialDateRange,
  previousRange: SocialDateRange,
): ReviewsReputationView {
  const data: SocialCommunity = {
    accountId: '',
    window: { from: previousRange.from, to: range.to },
    threads: reviewThreads,
    capped: false,
    generatedAt: '',
    range,
    previousRange,
  };
  return buildReviewsReputationForChannels(data, ['googlebusiness']);
}
